// @flow
import type {Background, User, Positions, PublishableOptions, PagedArray} from "./types.js";
import type {JSONContent} from "./jsontypes.js";
import Image from "./image.js";
import Page from "./page.js";
import Font from "./font.js";
import Api from "./index.js";
import processPositions from "./positions.js";

interface PublishableProps {
	id: string;
	uid: string;
	url: string;
	slug: string;

	load(): Promise<Publishable>;

	title: string;
	subTitle: string;
	description: string;
	shareDescription?: string;
	itemCount: number;
	publishDate: Date;
	options: PublishableOptions;
	image?: Image;
	imageOptions?: any;
	shareImage?: Image;
	publisher: User;
	loginRequired: boolean;
	likes?: number;
	context?: any;
	isSearchIndexingEnabled?: boolean;
}
export interface Story extends PublishableProps {
	type: "story";
	loaded: boolean;

	scrollDirection: "horizontal" | "vertical";
	flow: Page;
	pages: Array<Page>;
	// TODO: Instead of having an array of fonts here, create a `Fonts` Manager
	fonts: Array<Font>;
}

export interface Collection extends PublishableProps {
	type: "collection";
	loaded: boolean;

	content: Array<Publishable> | PagedArray<Publishable>;
	positions: Positions;
	background: Background;
}

export type Publishable = Story | Collection;

export class PublishableImpl {
	_api: Api;

	id: string;
	uid: string;
	url: string;
	slug: string;

	title: string;
	subTitle: string;
	description: string;
	shareDescription: string;
	itemCount: number;
	publishDate: Date;
	options: PublishableOptions;
	image: Image;
	imageOptions: any;
	shareImage: Image;
	publisher: User;
	href: string;
	tagfilter: Array<string>;
	tags: Array<any>;
	translations: Array<any>;
	locale: string;
	likes: number;
	context: any;
	isSearchIndexingEnabled: boolean;

	type: "story" | "collection" | "link";

	loaded: boolean;

	scrollDirection: "horizontal" | "vertical";
	flow: Page;
	pages: Array<Page>;
	// TODO: Instead of having an array of fonts here, create a `Fonts` Manager
	fonts: Array<Font>;

	content: Array<Publishable> | PagedArray<Publishable>;
	positions: Positions;
	background: Background;

	constructor(api: Api, id: string) {
		this._api = api;
		this.id = id;
	}

	merge(json: JSONContent) {
		if (this.loaded) { return; }

		this.uid = this.uid || json.id;
		this.loginRequired = !!this.loginRequired;
		this.url = this.url || json.url;
		this.slug = this.slug || json.slug;
		this.type = this.type || (json.type === "article" ? "story" : json.type === "link" ? "link" : "collection");
		this.title = this.title || json.title || "";
		this.subTitle = this.subTitle || json.subTitle || "";
		this.description = this.description || json.description || "";
		this.shareDescription = this.shareDescription || json.shareDescription || "";
		this.itemCount = this.itemCount || json.itemCount || 0;
		this.likes = this.likes || json.likes || 0;
		if (!this.publisher && json.publisher) {
			this.publisher = this._api._registerUser(json.publisher);
		}
		this.publishDate = this.publishDate || new Date(json.publishDate || Date.now());
		this.options = this.options || json.options || {};
		this.tagfilter = json.tagfilter || [];
		this.tags = json.tags || [];
		this.imageOptions = this.imageOptions || json.imageOptions || {};
		if (!this.image && json.image) {
			this.image = new Image(json.image);
		}
		if (!this.shareImage && json.shareImage) {
			this.shareImage = new Image(json.shareImage);
		}

		this.isSearchIndexingEnabled = this.isSearchIndexingEnabled === undefined ? json.isSearchIndexingEnabled !== false : this.isSearchIndexingEnabled;

		this.locale = this.locale || json.language || this._api.options.locale || 'en';
		const defaultTranslations = {default: {id: this.id, slug: this.slug}};
		defaultTranslations[this.locale] = {id: this.id, slug: this.slug};
		this.translations = this.translations || defaultTranslations;
		if (Array.isArray(json.translations)) {
			json.translations.forEach(t => {
				this.translations[t.language || 'default'] = {id: t.lookupId, slug: t.slug};
			});
		}

		if (!this.context && json.context) {
			this.context = json.context;
		}

		if (this.type === "story" || this.type === 'link') {
			if (json.baseUrl && json.flow && json.pages) {
				const {baseUrl} = json;
				this.loaded = true;
				this.flow = new Page(this._api, json.flow, baseUrl, undefined, this.options);
				this.pages = json.pages.map(page => new Page(this._api, page.content.url, baseUrl, page, this.options));
				this.scrollDirection = json.scrollDirection ||
					(this.pages.length === 1 && this.pages[0].size[1] > this.pages[0].size[0]
						? "vertical" : "horizontal");
			} else {
				this.loginRequired = (json.pages && json.flow);
			}
			
			if (json.assets && json.assets.fonts) {
				const baseUrl = json.assets.fontBaseUrl || "";
				this.fonts = json.assets.fonts.map(font => this._api._getFont(font, baseUrl));
			}
			if (json.data && json.data.href) {
				this.href = json.data.href;
				this.loaded = true;
			}
			if (json.backgroundColor) {
				this.backgroundColor = json.backgroundColor;
			}
		} else {
			if (this.type === 'collection') {
				this.loginRequired = !json.content;
			}
			if (json.background && (json.background.image || json.background.color)) {
				const {image, color} = json.background;
				this.background = {
					image: image && new Image(image),
					color,
				};
			}

			if (processPositions(this._api, this, json)) {
				this.loaded = true;
			}
		}
	}

	load(): Promise<Publishable> {
		return (this._api.resolve(this.id, undefined, this.type === 'collection' ? this.tagfilter : undefined): any);
	}
}



// WEBPACK FOOTER //
// ./lib/api/publishable.js